import React, { createContext, useContext } from "react";

// Create context with defaults for marketing theme
const ThemeContext = createContext({
  variant: "marketing", // marketing or dashboard
  primary: "market-teal",
  background: "bg-black",
  cardBackground: "bg-black/60",
  text: "text-white",
  textSecondary: "text-white/70",
  border: "border-market-teal/30",
  hover: "hover:bg-market-teal/20",
  glow: "shadow-market-teal/20",
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({
  children,
  variant = "marketing", // marketing or dashboard
}) => {
  // Define theme settings based on variant
  const themeValues = {
    variant,
    // Colors
    primary: variant === "marketing" ? "market-teal" : "blue-600",

    // Text
    text: variant === "marketing" ? "text-white" : "text-gray-800",
    textSecondary: variant === "marketing" ? "text-white/70" : "text-gray-600",

    // Backgrounds
    background: variant === "marketing" ? "bg-black" : "bg-gray-50",
    cardBackground: variant === "marketing" ? "bg-black/60" : "bg-white",

    // Borders
    border:
      variant === "marketing" ? "border-market-teal/30" : "border-gray-200",

    // Interactivity
    hover:
      variant === "marketing" ? "hover:bg-market-teal/20" : "hover:bg-gray-100",
    glow: variant === "marketing" ? "shadow-market-teal/20" : "shadow-blue-100",

    // Utility functions
    classNames: (...classes) => classes.filter(Boolean).join(" "),

    // Theme-specific CSS classes for common components
    getCardClass: (isInteractive = true) => {
      return variant === "marketing"
        ? `border-2 ${
            isInteractive
              ? "border-market-teal/30 bg-black/60 backdrop-blur-sm hover:border-market-teal/50 hover:shadow-md hover:shadow-market-teal/20"
              : "border-market-teal/20 bg-black/50 backdrop-blur-sm"
          }`
        : `border ${
            isInteractive
              ? "border-gray-200 bg-white hover:shadow-md hover:border-blue-200"
              : "border-gray-100 bg-white"
          }`;
    },

    getButtonClass: (variant = "primary") => {
      // Marketing theme buttons
      if (variant === "marketing") {
        if (variant === "primary")
          return "bg-market-teal text-black font-semibold hover:shadow-glow-sm transition-all";
        if (variant === "secondary")
          return "border border-white/20 text-white hover:bg-white/5 hover:border-market-teal/50 transition-all";
      }
      // Dashboard theme buttons
      else {
        if (variant === "primary")
          return "bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all";
        if (variant === "secondary")
          return "border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all";
      }
    },

    getHeadingClass: () => {
      return variant === "marketing"
        ? "font-bold text-white"
        : "font-semibold text-gray-900";
    },

    getLinkClass: () => {
      return variant === "marketing"
        ? "text-market-teal hover:text-market-teal/80 transition-colors"
        : "text-blue-600 hover:text-blue-800 transition-colors";
    },
  };

  return (
    <ThemeContext.Provider value={themeValues}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
